import { useMatrixEtaContext } from '@app/components/products/ProductMatrix/context';
import { ProductsEtaContext } from '@app/components/products/ProductsEta';
import { CancelSmall, Eta } from '@app/components/various/Icons';
import { Tooltip } from '@app/components/various/Tooltip';
import { useMatrixNavigation } from '@app/utils/hooks/matrix/useMatrixNavigation';
import { useIsTouchScreen } from '@app/utils/hooks/useIsTouchScreen';
import { isDefined } from '@app/utils/is';
import { isEmpty } from '@app/utils/isEmpty';
import { ItemEtasList, Position } from '@typings';
import cx from 'classnames';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { EtaTooltipContent } from '../MatrixContents/QuantityCellInfo/EtaTooltipContent';

import { RegularBodyCell } from './RegularBodyCell';

interface Props {
  itemEtas?: ItemEtasList;
  isDisabled?: boolean;
  position: Position;
  isPrepack?: boolean;
}

export const NoStockCell = ({ isDisabled, position, itemEtas, isPrepack = false }: Props) => {
  const isTouchScreen = useIsTouchScreen();
  const { isFocused, isSelected, setFocusableElement, isFocusTarget } = useMatrixNavigation(position);
  const { getShouldHighlightItemEta, isEtaVisible } = useMatrixEtaContext();
  const { legendColorPerEta } = React.useContext(ProductsEtaContext);
  const hasEta = isDefined(itemEtas) && !isEmpty(itemEtas);
  const isHighlighted = getShouldHighlightItemEta(itemEtas);
  const { t } = useTranslation(['orders']);
  const firstEta = itemEtas?.[0];
  const etaColor = isDefined(firstEta) ? legendColorPerEta[firstEta] : undefined;
  const shouldColorize = (isEtaVisible && !isHighlighted) || (isTouchScreen && !isDisabled);

  const bodyCellContent = React.useMemo(() => {
    if (hasEta) {
      return (
        <Tooltip content={<EtaTooltipContent itemEtas={itemEtas} />}>
          <span
            className="relative"
            style={
              shouldColorize ?
                {
                  color: etaColor,
                }
              : {}
            }
          >
            <Eta />
            {shouldColorize && <div className="absolute top-0 h-full w-full rounded-small bg-current opacity-15" />}
          </span>
        </Tooltip>
      );
    }

    if (isPrepack) {
      return (
        <Tooltip content={t('orders:not_available')}>
          <div>
            <CancelSmall className="text-neutral-40" />
          </div>
        </Tooltip>
      );
    }

    return <CancelSmall className="text-neutral-40" />;
  }, [hasEta, isPrepack, itemEtas, shouldColorize, etaColor, t]);

  return (
    <RegularBodyCell
      innerClassName={cx({
        'border-current': isHighlighted,
      })}
      isDisabled={isDisabled}
      ref={setFocusableElement}
      color={etaColor}
      isSelected={isFocused || isSelected}
      isFocusTarget={isFocusTarget}
      innerStyles={
        isHighlighted ?
          {
            color: etaColor,
          }
        : undefined
      }
    >
      {isHighlighted && <div className="absolute top-0 h-full w-full rounded-small bg-current opacity-15" />}
      {bodyCellContent}
      <div className="sr-only">0</div>
    </RegularBodyCell>
  );
};
